/** Documents & Certifications tab - document table with status chips and request/upload/
 * approve/reject/waive actions (local state only). Demo Mode only, local-only mock data -
 * see `supplierProfileData.ts`. */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { Section } from '../../../../../components/ui/Section';
import {
  getSupplierDocuments, SupplierDocumentItem, SupplierDocumentStatus,
} from '../../../../../mocks/data/supplierProfileData';

export interface DocumentsCertificationsTabProps {
  supplierId: number;
}

const STATUS_COLOR: Record<SupplierDocumentStatus, 'success' | 'warning' | 'error' | 'default' | 'info'> = {
  Required: 'default',
  Requested: 'info',
  Uploaded: 'info',
  UnderReview: 'warning',
  Approved: 'success',
  Expired: 'error',
  Rejected: 'error',
  Waived: 'default',
};

const DOCUMENT_TYPE_LABEL: Record<string, string> = {
  PublicLiabilityInsurance: 'Public liability insurance',
  FoodSafetyCertification: 'Food safety certification',
  BrcSalsaSts: 'BRC / SALSA / STS certification',
  HaccpDocuments: 'HACCP documents',
  ModernSlaveryStatement: 'Modern slavery statement',
  SustainabilityPolicy: 'Sustainability policy',
  AllergenPolicy: 'Allergen policy',
  BankDetails: 'Bank details',
  Contract: 'Contract',
};

const DocumentsCertificationsTab: React.FC<DocumentsCertificationsTabProps> = ({ supplierId }) => {
  const { t } = useTranslation('suppliers');
  const [documents, setDocuments] = useState<SupplierDocumentItem[]>(() => getSupplierDocuments(supplierId));

  useEffect(() => {
    setDocuments(getSupplierDocuments(supplierId));
  }, [supplierId]);

  const updateStatus = (id: number, status: SupplierDocumentStatus) => {
    setDocuments((current) => current.map((document) => (document.id === id ? { ...document, status, lastReviewed: new Date().toISOString().slice(0, 10) } : document)));
  };

  return (
    <Section>
      <Box sx={{ p: 4 }}>
        <Typography variant="textSM"
          fontWeight={600}
          sx={{ mb: 3 }}>{t('documentsCertifications', 'Documents & certifications')}</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('documentType', 'Document type')}</TableCell>
              <TableCell>{t('status', 'Status')}</TableCell>
              <TableCell>{t('issueDate', 'Issue date')}</TableCell>
              <TableCell>{t('expiryDate', 'Expiry date')}</TableCell>
              <TableCell>{t('owner', 'Owner')}</TableCell>
              <TableCell>{t('uploadedBy', 'Uploaded by')}</TableCell>
              <TableCell>{t('lastReviewed', 'Last reviewed')}</TableCell>
              <TableCell align="right">{t('actions', 'Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map((document) => (
              <TableRow key={document.id}>
                <TableCell>{t(`documentType${document.type}`, DOCUMENT_TYPE_LABEL[document.type] ?? document.type)}</TableCell>
                <TableCell><Chip label={document.status}
                  size="small"
                  color={STATUS_COLOR[document.status]} /></TableCell>
                <TableCell>{document.issueDate ?? '-'}</TableCell>
                <TableCell>{document.expiryDate ?? '-'}</TableCell>
                <TableCell>{document.owner ?? '-'}</TableCell>
                <TableCell>{document.uploadedBy ?? '-'}</TableCell>
                <TableCell>{document.lastReviewed ?? '-'}</TableCell>
                <TableCell align="right">
                  <Stack direction="row"
                    spacing={1}
                    justifyContent="flex-end">
                    {(document.status === 'Required') && (
                      <Button variant="secondary"
                        onClick={() => updateStatus(document.id, 'Requested')}>{t('requestDocument', 'Request')}</Button>
                    )}
                    {(document.status === 'Requested' || document.status === 'Uploaded') && (
                      <Button variant="secondary"
                        onClick={() => updateStatus(document.id, 'Uploaded')}>{t('uploadDocument', 'Upload')}</Button>
                    )}
                    {(document.status === 'Uploaded' || document.status === 'UnderReview') && (
                      <>
                        <Button variant="primary"
                          onClick={() => updateStatus(document.id, 'Approved')}>{t('approve', 'Approve')}</Button>
                        <Button variant="secondary"
                          onClick={() => updateStatus(document.id, 'Rejected')}>{t('reject', 'Reject')}</Button>
                      </>
                    )}
                    {document.status !== 'Approved' && document.status !== 'Waived' && (
                      <Button variant="secondary"
                        onClick={() => updateStatus(document.id, 'Waived')}>{t('waive', 'Waive')}</Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Section>
  );
};

export default DocumentsCertificationsTab;
